
import React, { useRef } from 'react'
import { toast } from 'sonner'
import SectionHeader from './SectionHeader'
import { Download, LoaderCircle, Plus, MessageSquare, X, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Input } from '../../components/input'
import { Send, Paperclip, ArrowUpIcon } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Button } from '@univdiam/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@univdiam/ui/components/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@univdiam/ui/components/alert-dialog'
import MediaRenderer from '../MediaUploader/MediaRender';
import { Avatar, AvatarFallback } from '@univdiam/ui/components/avatar';
import { AvatarImage } from '@radix-ui/react-avatar';
import { getFileIcon } from '../MediaUploader';
import { format } from 'date-fns';
import { formatTimeInUserTimezone } from '@univdiam/ui/lib/formatedDates';
import Cad2 from '../Icons/Cad2';
import RichTextEditor from '../RichTextEditor';
import { renderRichText, stripTrailingEmptyBlocks, richTextClass } from '@univdiam/ui/lib/renderRichText';
import { cn } from '@univdiam/ui/lib/utils';

type AttachmentProps = {

  url: string;
  name: string;
  size: number;
  type: string;
  file?: File;

}

type SendDataType = {
  message: string;
  media_url: string
  content_type: string
  metdata?: string
}


/** Mirrors EDIT_WINDOW_MINUTES in adminDiscussionService.js. */
const EDIT_WINDOW_MS = 15 * 60 * 1000

/**
 * Decides whether to *render* the Edit action. Enforcement lives in the API's
 * UPDATE ... WHERE clause — this check is cosmetic and trivially bypassed, so
 * it must never be the only thing standing between a user and an edit.
 *
 * A message whose window lapses while the page is open keeps showing Edit until
 * the next render. Saving it then fails with the API's 403, which the caller
 * surfaces as a toast. That is the intended fallback.
 */
const canEdit = (d: DiscussionMessage, currentUserId?: string) =>
  !!currentUserId &&
  String(d.user_id) === String(currentUserId) &&
  d.content_type === 'TEXT' &&
  !d.deleted_at &&
  !!d.created_at &&
  Date.now() - new Date(d.created_at).getTime() < EDIT_WINDOW_MS

const canDelete = (d: DiscussionMessage, currentUserId?: string) =>
  !!currentUserId &&
  String(d.user_id) === String(currentUserId) &&
  d.content_type === 'TEXT' &&
  !d.deleted_at

type DiscussionMessage = {
  created_at?: string;
  edited_at?: string | null;
  deleted_at?: string | null;
  media_url?: AttachmentProps[];
  id?: string;
  user_id?: string;
  sender_name?: string;
  sender_picture?: string;
  message: string
  content_type: string
  metadata: string
}

type DiscussionProps = {
  Discussion: DiscussionMessage[];
  isAdmin: boolean | null,
  handleSendToUpload: (files: File[]) => Promise<string[]>,
  mediaLaoding: boolean,
  isDicussionPending: boolean,
  isDiscussionLoading: boolean,
  handleSendDiscussion: (values: SendDataType) => void
  handleAddProductOpen: () => void
  handleShowProduct: (id: string) => void
  fetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  headerActions?: React.ReactNode
  /** Author id used to decide which messages expose edit/delete. */
  currentUserId?: string
  /** Omit both to hide the actions entirely (e.g. the customer app). */
  handleEditDiscussion?: (id: string, message: string) => Promise<void> | void
  handleDeleteDiscussion?: (id: string) => Promise<void> | void
}

function DiscussionSection({ Discussion, isAdmin, handleSendToUpload, mediaLaoding, handleSendDiscussion, isDicussionPending, isDiscussionLoading, handleAddProductOpen, handleShowProduct, fetchNextPage, hasNextPage, isFetchingNextPage, headerActions, currentUserId, handleEditDiscussion, handleDeleteDiscussion }: DiscussionProps) {
  const [comment, setComment] = useState('')
  const [pendingAttachments, setPendingAttachments] = useState<AttachmentProps[]>([])
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // id of the message being edited inline, and its working draft
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  // id pending delete confirmation. Held rather than deleting on click, so the
  // AlertDialog is the only path to an irreversible action.
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const startEdit = (d: DiscussionMessage) => {
    setEditingId(d.id!)
    setEditDraft(d.message)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditDraft('')
  }

  const saveEdit = async () => {
    if (!editingId || !handleEditDiscussion) return
    const next = stripTrailingEmptyBlocks(editDraft)
    if (next.replace(/<[^>]*>?/gm, '').trim() === '') return
    setIsSavingEdit(true)
    try {
      await handleEditDiscussion(editingId, next)
      cancelEdit()
    } catch {
      // The caller has already surfaced the error. Swallow it here so the
      // rejection does not escape this click handler, and leave the editor open
      // with the draft intact rather than discarding what was typed.
    } finally {
      setIsSavingEdit(false)
    }
  }

  const confirmDelete = async () => {
    if (!pendingDeleteId || !handleDeleteDiscussion) return
    setIsDeleting(true)
    try {
      await handleDeleteDiscussion(pendingDeleteId)
      setPendingDeleteId(null)
    } catch {
      // Caller toasted. Keep the dialog open so the action can be retried.
    } finally {
      setIsDeleting(false)
    }
  }
  
  // To keep track of previous scroll height before new messages are prepended
  const previousScrollHeightRef = useRef<number | null>(null);

  useEffect(() => {
    // When Discussion length changes (e.g. new page loaded)
    if (scrollContainerRef.current) {
      if (previousScrollHeightRef.current !== null) {
        // We prepended older messages, adjust scroll position
        const currentScrollHeight = scrollContainerRef.current.scrollHeight;
        scrollContainerRef.current.scrollTop = currentScrollHeight - previousScrollHeightRef.current;
        previousScrollHeightRef.current = null;
      } else {
        // Initial load or new message sent, scroll to bottom
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    }
  }, [Discussion]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (e.currentTarget.scrollTop === 0 && hasNextPage && !isFetchingNextPage && fetchNextPage) {
      previousScrollHeightRef.current = e.currentTarget.scrollHeight;
      fetchNextPage();
    }
  };

  // Mirrors the send button's disabled condition, so Enter cannot post an
  // empty comment or fire while an upload/post is already in flight.
  const handleSendIfAllowed = () => {
    const hasText = comment.replace(/<[^>]*>?/gm, '').trim() !== ''
    if (!hasText && pendingAttachments.length === 0) return
    if (isDicussionPending || mediaLaoding) return
    void handleSend()
  }

  const handleSend = async () => {
    let finalAttachments: Omit<AttachmentProps, 'file'>[] = []

    if (pendingAttachments.length > 0) {
      const filesToUpload = pendingAttachments.map(att => att.file as File)
      const uploadedUrls = (await handleSendToUpload(filesToUpload)) ?? []

      // A failed upload returns fewer URLs than files. Posting anyway stored
      // attachments with no URL and cleared the composer, so the files were
      // lost. Stop and keep everything for a retry (same rule as ChatSection).
      if (uploadedUrls.length !== filesToUpload.length) {
        toast.error('Attachments could not be uploaded. Nothing was sent, try again.')
        return
      }

      finalAttachments = pendingAttachments.map((att, idx) => ({
        url: uploadedUrls[idx]!,
        name: att.name,
        size: att.size,
        type: att.type
      }))
    }

    const data = {
      message: stripTrailingEmptyBlocks(comment),
      media_url: JSON.stringify(finalAttachments) as any,
      content_type: 'TEXT'
    }
    handleSendDiscussion(data)
    setComment('')
    setPendingAttachments([])
  };




  const addFiles = (newFiles: File[]) => {

    const filePreviews = newFiles.map(file => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      type: file.type || file.name.split('.').pop()!
    }));

    setPendingAttachments(prev => [...prev, ...filePreviews]);
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const removePendingAttachment = (indexToRemove: number) => {
    setPendingAttachments(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[indexToRemove].url);
      updated.splice(indexToRemove, 1);
      return updated;
    });
  };


  return (
    <div className="flex flex-col bg-white border border-[#E9EAEB] rounded-md text-caption1 overflow-hidden h-full">
      <SectionHeader
        title="Discussion"
        sectionIcon={<MessageSquare strokeWidth={1.5} className="w-5 h-5 text-gray-600" />}
        isAdmin={isAdmin}
        buttonText="Add a product"
        headerActions={headerActions}
        onButtonClick={() => {
          handleAddProductOpen()
        }}
      />

      <div className="p-4 flex flex-col grow shrink min-h-0 overflow-y-auto relative" ref={scrollContainerRef} onScroll={handleScroll}>
        {isFetchingNextPage && (
          <div className="flex justify-center p-2 mb-2">
            <LoaderCircle className="animate-spin text-muted-foreground" size={18} />
          </div>
        )}
        {
          isDiscussionLoading ? <div className="flex-1 flex justify-center items-center"><LoaderCircle className='animate-spin' size={18} /></div> : <div>
            {Discussion.map((d) => {
              const isDeleted = !!d.deleted_at
              const isEditing = editingId === d.id
              const showEdit = !!handleEditDiscussion && canEdit(d, currentUserId)
              const showDelete = !!handleDeleteDiscussion && canDelete(d, currentUserId)

              return (
              <div
                key={d.id}
                className={cn(
                  "group/msg flex gap-3 items-start gap-[16px] mb-[16px]",
                  // Negative margin cancels the padding, so the tint bleeds to
                  // the thread's edges instead of leaving a gutter.
                  "-mx-2 px-2 py-1 rounded-md transition-colors",
                  // The actions menu only appears on hover, so without this the
                  // row it belongs to is ambiguous. Keep the tint while the menu
                  // is open, since the pointer has left the row by then.
                  !isEditing && "hover:bg-gray-50 has-[[data-state=open]]:bg-gray-50"
                )}
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={d.sender_picture} alt={d.sender_name} className='w-full h-full' />
                  <AvatarFallback>{d.sender_name?.charAt(0)}</AvatarFallback>
                </Avatar>

                <div className="flex flex-col flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{d.sender_name}</p>
                    <span className="text-sm text-muted-foreground">
                      {d.created_at ? <span>{format(new Date(d.created_at), 'dd MMM yyyy')} &#x2022;  {formatTimeInUserTimezone(d.created_at)}</span> : '—'}
                    </span>
                    {d.edited_at && !isDeleted && (
                      <span className="text-xs text-muted-foreground italic">(edited)</span>
                    )}

                    {(showEdit || showDelete) && !isEditing && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            aria-label="Message actions"
                            className="ml-auto p-1 rounded hover:bg-gray-100 text-gray-500 opacity-0 group-hover/msg:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity shrink-0"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {showEdit && (
                            <DropdownMenuItem onSelect={() => startEdit(d)}>
                              <Pencil className="w-3.5 h-3.5" /> Edit
                            </DropdownMenuItem>
                          )}
                          {showDelete && (
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() => setPendingDeleteId(d.id!)}
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>

                  {isDeleted ? (
                    <p className="mt-1 text-md italic text-muted-foreground">This message was deleted</p>
                  ) : isEditing ? (
                    <div className="mt-1 w-full">
                      <RichTextEditor
                        value={editDraft}
                        onChange={setEditDraft}
                        className="bg-transparent border border-[#DBDBDB]/50 rounded-[6px]"
                        editorClass="min-h-[40px] max-h-[200px] overflow-y-auto overflow-x-hidden px-[6px] py-[3px] text-caption1 break-words [overflow-wrap:anywhere] whitespace-pre-wrap"
                      />
                      <div className="flex items-center gap-2 mt-1">
                        <Button size="sm" onClick={saveEdit} disabled={isSavingEdit}>
                          {isSavingEdit ? <LoaderCircle className="size-3.5 animate-spin" /> : 'Save'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={isSavingEdit}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={cn("mt-1 text-md break-words [overflow-wrap:anywhere] whitespace-pre-wrap [&_p]:min-h-[1rem] w-fit max-w-full overflow-hidden", richTextClass)}
                      dangerouslySetInnerHTML={{ __html: renderRichText(d.message) }}
                    />
                  )}

                  {!isDeleted && Array.isArray(d.media_url) && d.media_url.length > 0 && (
                    <div className="mt-2 flex flex-col gap-2">
                      {d.media_url.map((att: AttachmentProps) => (
                        <AttachmentLink key={att.url} {...att} />
                      ))}
                    </div>
                  )}
                  {
                    !isDeleted && d.content_type === 'CAD' && <div className='mt-[4px] flex items-center w-fit min-w-[250px] gap-[10px] px-[16px] py-[8px] cursor-pointer bg-[#F6F6F6] border border-[#E9EAEB] rounded-[6px]' onClick={() => handleShowProduct(d.id!)}>
                      <Cad2 />
                      <p className='caption font-medium'>Product Design</p>
                    </div>
                  }
                </div>
              </div>
              )
            })}
          </div>
        }

      </div>
      <div className="flex flex-col w-full items-start border-t border-[#E9EAEB] mt-auto">
        {pendingAttachments.length > 0 &&
          <div className="flex gap-[8px] flex-wrap bg-[#f6f6f6] w-full p-2 rounded overflow-x-auto border">
            {pendingAttachments.map((media, idx) => (
              <div key={media.url} className="relative group/media">
                <MediaRenderer file={media} className="!h-[52px] !w-[52px]" />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removePendingAttachment(idx);
                  }}
                  className="bg-red-500 rounded-full text-white w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover/media:opacity-100 transition-opacity absolute -top-1.5 -right-1.5"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
            <div
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              className="w-[52px] h-[52px] rounded-[4px] border border-[#DBDBDB] flex items-center justify-center cursor-pointer hover:bg-[#f0f0f0] transition"
            >
              {mediaLaoding ? <Plus className="opacity-50" /> : <Plus />}
            </div>
          </div>
        }
        {/* No onSubmit handler existed, so any implicit submission (Enter in a
            text input) navigated away and reloaded the page. */}
        <form onSubmit={(e) => e.preventDefault()} className="flex w-full items-end gap-2 p-6 pt-4 pb-4">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-1.5 hover:bg-gray-100 rounded-full text-gray-500 transition mb-1 shrink-0"
            disabled={mediaLaoding || isDicussionPending}
          >
            <Paperclip className="cursor-pointer w-5 h-5" />
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUpload}
            className="hidden"
            disabled={mediaLaoding || isDicussionPending}
            accept=".jpg,.jpeg,.png,.zip,.cad,.pdf,.heic,.heif"
            multiple
          />
          <div className="relative w-full flex-1">
            <RichTextEditor
              onEnter={handleSendIfAllowed}
              onPasteFiles={addFiles}
              value={comment}
              onChange={setComment}
              placeholder="Add a comment..."
              className="bg-transparent border border-[#DBDBDB]/50 rounded-[6px] shadow-[inset_0.38px_-0.38px_3.02px_0px_rgba(0,0,0,0.12)] focus-within:ring-2 focus-within:ring-ring/50 focus-within:border-transparent transition-all overflow-hidden"
              editorClass="min-h-[40px] max-h-[150px] overflow-y-auto overflow-x-hidden px-[6px] py-[3px] text-caption1 pr-8 break-words [overflow-wrap:anywhere] whitespace-pre-wrap"
            />
          </div>
          
          <Button
            type="button"
            size="icon"
            className="p-1 rounded-full shrink-0 mb-1"
            disabled={(pendingAttachments.length === 0 && comment.replace(/<[^>]*>?/gm, '').trim() === '') || mediaLaoding || isDicussionPending}
            onClick={(e) => {
              e.preventDefault();
              if (isDicussionPending || mediaLaoding) return;
              handleSend()
            }}
          >
            {isDicussionPending || mediaLaoding ? <LoaderCircle className='size-3.5 animate-spin' /> : <ArrowUpIcon className="size-3.5" />}
          </Button>
        </form>
      </div>

      {/* Deleting is irreversible from the UI's point of view (the row is soft
          deleted, but nothing here can restore it), so it is gated behind an
          explicit confirmation rather than a bare menu click. */}
      <AlertDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => { if (!open && !isDeleting) setPendingDeleteId(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              This message will be removed from the discussion for everyone. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600/20"
              onClick={(e) => { e.preventDefault(); void confirmDelete() }}
            >
              {isDeleting ? <LoaderCircle className="size-3.5 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>

  )
}

export default DiscussionSection


export function AttachmentLink({
  url,
  name,
  size,
}: AttachmentProps) {
  return (
    <div className="bg-white border border-[#E9EAEB] rounded-md px-2.5 py-1.5 flex justify-between items-center gap-2">
      <div className="flex items-center gap-2 flex-1">
        <span className="w-6 h-6">{getFileIcon(url)}</span>
        <div className="truncate">
          <p className="text-sm font-medium truncate">{name}</p>
          <p className="text-xs text-muted-foreground">
            {(size / (1024 * 1024)).toFixed(2)} MB
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#5C6BC0] hover:bg-[#88373430] p-1 rounded-sm"
          title="Download"
        >
          <Download size={18} strokeWidth={1} />
        </a>
      </div>
    </div>
  );
}